import { type Ref, useCallback, useEffect, useRef, useState } from 'react'
import './masthead.css'

/**
 * The masthead an org drew for its public home, drawn in a frame of its own.
 *
 * The frame keeps the org's styles off the page and the page's off the
 * masthead, and runs no script: the sandbox grants the same origin only so the
 * page can read the drawing's height, which a frame cannot report by itself.
 * Its media queries answer the frame's width, which is the page's measure.
 *
 * The height follows the drawing's own box rather than the frame's, so a face
 * or an image settling after load grows the frame with it. The page's heading
 * stays in the page: what the frame holds is a separate document to a reader
 * walking the headings.
 */
export function Masthead({
  html,
  title,
  ref,
}: {
  html: string
  title: string
  ref?: Ref<HTMLElement>
}) {
  const frame = useRef<HTMLIFrameElement>(null)
  const watch = useRef<ResizeObserver | null>(null)
  const [height, setHeight] = useState<number | null>(null)

  const follow = useCallback(() => {
    const root = frame.current?.contentDocument?.documentElement
    if (!root) return
    watch.current?.disconnect()
    watch.current = new ResizeObserver(() => setHeight(root.scrollHeight))
    watch.current.observe(root)
    setHeight(root.scrollHeight)
  }, [])

  useEffect(() => () => watch.current?.disconnect(), [])

  return (
    <section className="masthead" aria-labelledby="home-hero" ref={ref}>
      <h1 id="home-hero" className="sr-only">
        {title}
      </h1>
      <div className="home__measure">
        <iframe
          ref={frame}
          className="masthead__frame"
          title={title}
          sandbox="allow-same-origin"
          srcDoc={html}
          onLoad={follow}
          style={height === null ? undefined : { height }}
        />
      </div>
    </section>
  )
}
